// Chemin: C:\gestion-copro\src\services\verification.service.ts
// Session 67 : Creation module Verification Comptable
// Session 68 : Ajout lancerAnalyseComptable pour appel Cloud Function SENTINELLE
// Session 70 : Ajout updateControle pour integrations Courriers/Affaires
// Session C  : AppelsFonds, Regularisations, SyntheseComptable, fix deleteVerification
// Reference : CADRAGE_MODULE_VERIFICATION_COMPTABLE_V1.md

import {
  collection,
  doc,
  addDoc,
  updateDoc,
  deleteDoc,
  getDoc,
  getDocs,
  query,
  where,
  orderBy,
  Timestamp,
  writeBatch,
} from 'firebase/firestore';
import { httpsCallable } from 'firebase/functions';
import { db, functions } from './firebase';
import {
  Verification,
  Controle,
  CreateVerificationData,
  UpdateVerificationData,
  CreateControleData,
  TraiterControleData,
  StatutVerification,
  VerificationResume,
  ResultatAnalyseComptable,
  calculerScoreConformite,
  TYPE_CONTROLE_NIVEAUX,
  AppelFonds,
  Regularisation,
  SyntheseComptable,
  CreateAppelFondsData,
  CreateRegularisationData,
  detecterTrimestre,
} from '../types/verification.types';
export type { ResultatAnalyseComptable } from '../types/verification.types';
export type { PropositionAppelFonds, PropositionRegularisation };

// ============================================
// TYPES POUR L'ANALYSE SENTINELLE
// ============================================

interface ControleGenere {
  type: string;
  resultat: 'ok' | 'attention' | 'anomalie';
  description: string;
  regleAppliquee: string;
  montant?: number;
  montantAttendu?: number;
  ecart?: number;
  pourcentageEcart?: number;
  fournisseur?: string;
  libelle?: string;
  dateDocument?: string;
  documentSourceId?: string;
}

interface ExtractionComptable {
  documentId: string;
  documentNom: string;
  typeComptable: string;
  exercice: string | null;
  totalGeneral: number | null;
  lignes: Array<{
    libelle: string;
    montant: number;
    date?: string;
    poste?: string;
    reference?: string;
  }>;
  totauxParPoste: Record<string, number>;
  metadata: {
    dateDocument: string | null;
    emetteur: string | null;
    destinataire: string | null;
  };
}

interface PropositionAppelFonds {
  documentId: string;
  documentNom: string;
  trimestre: string;
  exercice: number;
  montantAppele: number;
  dateAppel: string | null;
}

interface PropositionRegularisation {
  documentId: string;
  documentNom: string;
  exerciceRegularise: number;
  montantReel: number;
  estTropPercu: boolean;
}

interface AnalyseComptabiliteResponse {
  success: boolean;
  documentsAnalyses: number;
  controlesGeneres: ControleGenere[];
  extractionsRaw: ExtractionComptable[];
  erreurs: string[];
  tokensUtilises: number;
  appelsFondsCreees?: number;
  regularisationsCreees?: number;
  propositionsAppelsFonds?: PropositionAppelFonds[];
  propositionsRegularisations?: PropositionRegularisation[];
}


// ============================================
// HELPERS
// ============================================

/**
 * Chemin de la collection verifications pour une copropriete
 */
function getVerificationsPath(coproprieteId: string): string {
  return `coproprietes/${coproprieteId}/verifications`;
}

/**
 * Chemin de la sous-collection controles pour une verification
 */
function getControlesPath(coproprieteId: string, verificationId: string): string {
  return `coproprietes/${coproprieteId}/verifications/${verificationId}/controles`;
}

/**
 * Supprime les proprietes undefined d'un objet
 */
function removeUndefinedFields<T extends Record<string, unknown>>(obj: T): Partial<T> {
  const result: Partial<T> = {};
  for (const key of Object.keys(obj) as Array<keyof T>) {
    if (obj[key] !== undefined) {
      result[key] = obj[key];
    }
  }
  return result;
}

// ============================================
// VERIFICATIONS - CRUD
// ============================================

/**
 * Cree une nouvelle verification comptable
 */
export async function createVerification(
  data: CreateVerificationData,
  userId: string,
  userNom: string
): Promise<string> {
  const now = Timestamp.now();
  
  const verification: Omit<Verification, 'id'> = {
    coproprieteId: data.coproprieteId,
    exercice: data.exercice,
    titre: data.titre,
    documentsIds: data.documentsIds || [],
    scoreConformite: 100, // Score initial parfait (aucun controle encore)
    controlesTotal: 0,
    controlesOK: 0,
    controlesAttention: 0,
    controlesAnomalie: 0,
    statut: 'en_cours',
    dateCreation: now,
    dateModification: now,
    createdBy: userId,
    createdByNom: userNom,
  };
  
  // Nettoyer les champs undefined
  const cleanData = removeUndefinedFields(verification as Record<string, unknown>);
  
  const collectionRef = collection(db, getVerificationsPath(data.coproprieteId));
  const docRef = await addDoc(collectionRef, cleanData);
  
  return docRef.id;
}

/**
 * Recupere une verification par son ID
 */
export async function getVerificationById(
  coproprieteId: string,
  verificationId: string
): Promise<Verification | null> {
  const docRef = doc(db, getVerificationsPath(coproprieteId), verificationId);
  const snapshot = await getDoc(docRef);
  
  if (!snapshot.exists()) {
    return null;
  }
  
  return {
    id: snapshot.id,
    ...snapshot.data(),
  } as Verification;
}

/**
 * Recupere toutes les verifications d'une copropriete
 */
export async function getVerificationsByCopropriete(
  coproprieteId: string,
  statutFilter?: StatutVerification
): Promise<Verification[]> {
  const q = query(
    collection(db, getVerificationsPath(coproprieteId)),
    orderBy('dateCreation', 'desc')
  );
  
  const snapshot = await getDocs(q);
  let verifications = snapshot.docs.map((doc) => ({
    id: doc.id,
    ...doc.data(),
  })) as Verification[];
  
  // Filtrage cote client si statut specifie
  if (statutFilter) {
    verifications = verifications.filter((v) => v.statut === statutFilter);
  }
  
  return verifications;
}

/**
 * Recupere les resumes des verifications pour affichage liste
 */
export async function getVerificationsResumes(
  coproprieteId: string
): Promise<VerificationResume[]> {
  const verifications = await getVerificationsByCopropriete(coproprieteId);
  
  return verifications.map((v) => ({
    id: v.id,
    exercice: v.exercice,
    titre: v.titre,
    scoreConformite: v.scoreConformite,
    controlesOK: v.controlesOK,
    controlesAttention: v.controlesAttention,
    controlesAnomalie: v.controlesAnomalie,
    statut: v.statut,
    dateCreation: v.dateCreation,
    nombreDocuments: v.documentsIds.length,
    // Session 263 : Compteurs cockpit manquants — fix affichage carte liste
    montantTotalAppele: (v as any).montantTotalAppele ?? 0,
    montantTotalRegle: (v as any).montantTotalRegle ?? 0,
    nombreAppelsFonds: (v as any).nombreAppelsFonds ?? 0,
    aRegularisation: (v as any).aRegularisation ?? false,
  }));
}

/**
 * Met a jour une verification
 */
export async function updateVerification(
  coproprieteId: string,
  verificationId: string,
  data: UpdateVerificationData
): Promise<void> {
  const docRef = doc(db, getVerificationsPath(coproprieteId), verificationId);
  
  const cleanData = removeUndefinedFields(data as Record<string, unknown>);
  
  await updateDoc(docRef, {
    ...cleanData,
    dateModification: Timestamp.now(),
  });
}

/**
 * Cloture une verification
 */
export async function cloturerVerification(
  coproprieteId: string,
  verificationId: string,
  userId: string,
  userNom: string
): Promise<void> {
  const docRef = doc(db, getVerificationsPath(coproprieteId), verificationId);
  
  await updateDoc(docRef, {
    statut: 'termine',
    dateCloture: Timestamp.now(),
    closedBy: userId,
    closedByNom: userNom,
    dateModification: Timestamp.now(),
  });
}

/**
 * Archive une verification
 */
export async function archiverVerification(
  coproprieteId: string,
  verificationId: string
): Promise<void> {
  const docRef = doc(db, getVerificationsPath(coproprieteId), verificationId);
  
  await updateDoc(docRef, {
    statut: 'archive',
    dateModification: Timestamp.now(),
  });
}

/**
 * Supprime une verification et ses controles
 */
export async function deleteVerification(
  coproprieteId: string,
  verificationId: string
): Promise<void> {
  const batch = writeBatch(db);

  // Supprimer les controles
  const controlesRef = collection(db, getControlesPath(coproprieteId, verificationId));
  const controlesSnapshot = await getDocs(controlesRef);
  controlesSnapshot.docs.forEach((d) => { batch.delete(d.ref); });

  // Supprimer les appels de fonds (Session C)
  const appelsFondsRef = collection(
    db,
    `coproprietes/${coproprieteId}/verifications/${verificationId}/appelsFonds`
  );
  const appelsFondsSnapshot = await getDocs(appelsFondsRef);
  appelsFondsSnapshot.docs.forEach((d) => { batch.delete(d.ref); });

  // Supprimer les regularisations (Session C)
  const regularisationsRef = collection(
    db,
    `coproprietes/${coproprieteId}/verifications/${verificationId}/regularisations`
  );
  const regularisationsSnapshot = await getDocs(regularisationsRef);
  regularisationsSnapshot.docs.forEach((d) => { batch.delete(d.ref); });

  // Supprimer la verification elle-meme
  const verificationRef = doc(db, getVerificationsPath(coproprieteId), verificationId);
  batch.delete(verificationRef);

  await batch.commit();
}

/**
 * Ajoute un document a une verification
 */
export async function ajouterDocumentVerification(
  coproprieteId: string,
  verificationId: string,
  documentId: string
): Promise<void> {
  const verification = await getVerificationById(coproprieteId, verificationId);
  if (!verification) throw new Error('Verification non trouvee');
  
  if (verification.documentsIds.includes(documentId)) {
    return; // Deja ajoute
  }
  
  const docRef = doc(db, getVerificationsPath(coproprieteId), verificationId);
  await updateDoc(docRef, {
    documentsIds: [...verification.documentsIds, documentId],
    dateModification: Timestamp.now(),
  });
}

/**
 * Retire un document d'une verification
 */
export async function retirerDocumentVerification(
  coproprieteId: string,
  verificationId: string,
  documentId: string
): Promise<void> {
  const verification = await getVerificationById(coproprieteId, verificationId);
  if (!verification) throw new Error('Verification non trouvee');
  
  const docRef = doc(db, getVerificationsPath(coproprieteId), verificationId);
  await updateDoc(docRef, {
    documentsIds: verification.documentsIds.filter((id) => id !== documentId),
    dateModification: Timestamp.now(),
  });
}

// ============================================
// CONTROLES - CRUD
// ============================================

/**
 * Ajoute un controle a une verification
 */
export async function addControle(
  coproprieteId: string,
  verificationId: string,
  data: CreateControleData
): Promise<string> {
  const now = Timestamp.now();
  
  const controle: Omit<Controle, 'id'> = {
    type: data.type,
    niveau: TYPE_CONTROLE_NIVEAUX[data.type],
    resultat: data.resultat,
    description: data.description,
    regleAppliquee: data.regleAppliquee,
    montant: data.montant,
    montantAttendu: data.montantAttendu,
    ecart: data.ecart,
    pourcentageEcart: data.pourcentageEcart,
    fournisseur: data.fournisseur,
    libelle: data.libelle,
    dateDocument: data.dateDocument ? Timestamp.fromDate(data.dateDocument) : undefined,
    documentSourceId: data.documentSourceId,
    statut: 'nouveau',
    dateDetection: now,
  };
  
  const cleanData = removeUndefinedFields(controle as Record<string, unknown>);
  
  const collectionRef = collection(db, getControlesPath(coproprieteId, verificationId));
  const docRef = await addDoc(collectionRef, cleanData);
  
  // Mettre a jour les compteurs de la verification
  await recalculerCompteurs(coproprieteId, verificationId);
  
  return docRef.id;
}

/**
 * Ajoute plusieurs controles en batch (apres analyse SENTINELLE)
 */
export async function addControlesBatch(
  coproprieteId: string,
  verificationId: string,
  controlesData: CreateControleData[]
): Promise<string[]> {
  const batch = writeBatch(db);
  const now = Timestamp.now();
  const ids: string[] = [];
  
  const collectionRef = collection(db, getControlesPath(coproprieteId, verificationId));
  
  for (const data of controlesData) {
    const controle: Omit<Controle, 'id'> = {
      type: data.type,
      niveau: TYPE_CONTROLE_NIVEAUX[data.type],
      resultat: data.resultat,
      description: data.description,
      regleAppliquee: data.regleAppliquee,
      montant: data.montant,
      montantAttendu: data.montantAttendu,
      ecart: data.ecart,
      pourcentageEcart: data.pourcentageEcart,
      fournisseur: data.fournisseur,
      libelle: data.libelle,
      dateDocument: data.dateDocument ? Timestamp.fromDate(data.dateDocument) : undefined,
      documentSourceId: data.documentSourceId,
      statut: 'nouveau',
      dateDetection: now,
    };
    
    const cleanData = removeUndefinedFields(controle as Record<string, unknown>);
    const newDocRef = doc(collectionRef);
    batch.set(newDocRef, cleanData);
    ids.push(newDocRef.id);
  }
  
  await batch.commit();
  
  // Mettre a jour les compteurs
  await recalculerCompteurs(coproprieteId, verificationId);
  
  return ids;
}

/**
 * Recupere tous les controles d'une verification
 */
export async function getControlesByVerification(
  coproprieteId: string,
  verificationId: string
): Promise<Controle[]> {
  const q = query(
    collection(db, getControlesPath(coproprieteId, verificationId)),
    orderBy('dateDetection', 'desc')
  );
  
  const snapshot = await getDocs(q);
  return snapshot.docs.map((doc) => ({
    id: doc.id,
    ...doc.data(),
  })) as Controle[];
}

/**
 * Recupere un controle par son ID
 */
export async function getControleById(
  coproprieteId: string,
  verificationId: string,
  controleId: string
): Promise<Controle | null> {
  const docRef = doc(db, getControlesPath(coproprieteId, verificationId), controleId);
  const snapshot = await getDoc(docRef);
  
  if (!snapshot.exists()) {
    return null;
  }
  
  return {
    id: snapshot.id,
    ...snapshot.data(),
  } as Controle;
}

/**
 * Traite un controle (action du CS)
 */
export async function traiterControle(
  coproprieteId: string,
  verificationId: string,
  controleId: string,
  data: TraiterControleData,
  userId: string,
  userNom: string
): Promise<void> {
  const docRef = doc(db, getControlesPath(coproprieteId, verificationId), controleId);
  
  const updateData = {
    statut: data.statut,
    noteCS: data.noteCS,
    justificatifUrl: data.justificatifUrl,
    courrierId: data.courrierId,
    affaireId: data.affaireId,
    dateTraitement: Timestamp.now(),
    traitePar: userId,
    traiteParNom: userNom,
  };
  
  const cleanData = removeUndefinedFields(updateData);
  await updateDoc(docRef, cleanData);
}

/**
 * Met a jour un controle (ajout courrierId, affaireId, etc.)
 * Session 70 : Integration Courriers/Affaires
 */
export async function updateControle(
  coproprieteId: string,
  verificationId: string,
  controleId: string,
  data: {
    courrierId?: string;
    affaireId?: string;
    statut?: 'nouveau' | 'justifie' | 'conteste' | 'signale_ag';
    noteCS?: string;
  }
): Promise<void> {
  const controleRef = doc(
    db,
    'coproprietes',
    coproprieteId,
    'verifications',
    verificationId,
    'controles',
    controleId
  );

  // Construire l'objet de mise a jour sans les undefined
  const updateData: Record<string, any> = {
    dateTraitement: Timestamp.now(),
  };

  if (data.courrierId !== undefined) {
    updateData.courrierId = data.courrierId;
  }
  if (data.affaireId !== undefined) {
    updateData.affaireId = data.affaireId;
  }
  if (data.statut !== undefined) {
    updateData.statut = data.statut;
  }
  if (data.noteCS !== undefined) {
    updateData.noteCS = data.noteCS;
  }

  await updateDoc(controleRef, updateData);
}

/**
 * Supprime un controle
 */
export async function deleteControle(
  coproprieteId: string,
  verificationId: string,
  controleId: string
): Promise<void> {
  const docRef = doc(db, getControlesPath(coproprieteId, verificationId), controleId);
  await deleteDoc(docRef);
  
  // Recalculer les compteurs
  await recalculerCompteurs(coproprieteId, verificationId);
}

// ============================================
// RECALCUL DES COMPTEURS
// ============================================

/**
 * Recalcule les compteurs et le score d'une verification
 */
export async function recalculerCompteurs(
  coproprieteId: string,
  verificationId: string
): Promise<void> {
  const controles = await getControlesByVerification(coproprieteId, verificationId);
  
  let controlesOK = 0;
  let controlesAttention = 0;
  let controlesAnomalie = 0;
  
  for (const controle of controles) {
    switch (controle.resultat) {
      case 'ok':
        controlesOK++;
        break;
      case 'attention':
        controlesAttention++;
        break;
      case 'anomalie':
        controlesAnomalie++;
        break;
    }
  }
  
  const scoreConformite = calculerScoreConformite(
    controlesOK,
    controlesAttention,
    controlesAnomalie
  );
  
  const docRef = doc(db, getVerificationsPath(coproprieteId), verificationId);
  await updateDoc(docRef, {
    controlesTotal: controles.length,
    controlesOK,
    controlesAttention,
    controlesAnomalie,
    scoreConformite,
    dateModification: Timestamp.now(),
  });
}

// ============================================
// ANALYSE SENTINELLE COMPTABILITE
// ============================================

/**
 * Lance l'analyse SENTINELLE sur les documents d'une verification
 * Appelle la Cloud Function analyzeComptabilite et enregistre les controles
 */
export async function lancerAnalyseComptable(
  coproprieteId: string,
  verificationId: string,
  exercice: string
): Promise<ResultatAnalyseComptable> {
  // Appel de la Cloud Function
  const analyzeComptabilite = httpsCallable<
    { coproprieteId: string; verificationId: string; exercice: string },
    AnalyseComptabiliteResponse
  >(functions, 'analyzeComptabilite');

  const result = await analyzeComptabilite({
    coproprieteId,
    verificationId,
    exercice,
  });

  const response = result.data;

  if (!response.success) {
    return {
      success: false,
      documentsAnalyses: 0,
      controlesAjoutes: 0,
      appelsFondsCreees: 0,
      regularisationsCreees: 0,
      erreurs: response.erreurs || ["Erreur inconnue lors de l'analyse"],
      tokensUtilises: 0,
    };
  }

  // Convertir les controles generes en CreateControleData
  const controlesData: CreateControleData[] = response.controlesGeneres.map((c) => ({
    type: c.type as CreateControleData['type'],
    resultat: c.resultat,
    description: c.description,
    regleAppliquee: c.regleAppliquee,
    montant: c.montant,
    montantAttendu: c.montantAttendu,
    ecart: c.ecart,
    pourcentageEcart: c.pourcentageEcart,
    fournisseur: c.fournisseur,
    libelle: c.libelle,
    dateDocument: c.dateDocument ? new Date(c.dateDocument) : undefined,
    documentSourceId: c.documentSourceId,
  }));

  // Enregistrer les controles en batch
  let controlesAjoutes = 0;
  if (controlesData.length > 0) {
    const ids = await addControlesBatch(coproprieteId, verificationId, controlesData);
    controlesAjoutes = ids.length;
  }

  // Marquer la verification comme analysee
  const docRef = doc(db, getVerificationsPath(coproprieteId), verificationId);
  await updateDoc(docRef, {
    derniereAnalyse: Timestamp.now(),
    tokensUtilises: (response.tokensUtilises || 0),
    dateModification: Timestamp.now(),
  });

  return {
    success: true,
    documentsAnalyses: response.documentsAnalyses,
    controlesAjoutes,
    appelsFondsCreees: 0,
    regularisationsCreees: 0,
    erreurs: response.erreurs || [],
    tokensUtilises: response.tokensUtilises || 0,
    propositionsAppelsFonds: response.propositionsAppelsFonds || [],
    propositionsRegularisations: response.propositionsRegularisations || [],
  };
}

// ============================================
// VALIDATION HUMAINE ET ÉCRITURE EN FIRESTORE
// ============================================

export async function validerEtEnregistrerAnalyse(
  coproprieteId: string,
  verificationId: string,
  appelsFonds: PropositionAppelFonds[],
  regularisations: PropositionRegularisation[]
): Promise<{ appelsFondsCreees: number; regularisationsCreees: number }> {
  const batch = writeBatch(db);
  const now = Timestamp.now();

  // ── Supprimer les anciens appels de fonds avant réécriture ──
  const oldAppelsRef = collection(
    db,
    `coproprietes/${coproprieteId}/verifications/${verificationId}/appelsFonds`
  );
  const oldAppelsSnap = await getDocs(oldAppelsRef);
  oldAppelsSnap.docs.forEach((d) => batch.delete(d.ref));

  // ── Supprimer les anciennes régularisations avant réécriture ──
  const oldRegsRef = collection(
    db,
    `coproprietes/${coproprieteId}/verifications/${verificationId}/regularisations`
  );
  const oldRegsSnap = await getDocs(oldRegsRef);
  oldRegsSnap.docs.forEach((d) => batch.delete(d.ref));

  await batch.commit();

  // ── Écrire les appels de fonds validés ──
  const batchEcriture = writeBatch(db);
  let montantTotalAppele = 0;

  for (const af of appelsFonds) {
    const appelRef = doc(
      collection(db, `coproprietes/${coproprieteId}/verifications/${verificationId}/appelsFonds`)
    );
    batchEcriture.set(appelRef, {
      montantAppele: af.montantAppele,
      montantRegle: 0,
      trimestre: af.trimestre,
      exercice: af.exercice,
      statut: 'a_regler',
      documentSourceId: af.documentId,
      documentNom: af.documentNom,
      libelle: af.documentNom,
      ventilationPostes: [],
      dateCreation: now,
      dateModification: now,
    });
    montantTotalAppele += af.montantAppele;
  }

  // ── Écrire les régularisations validées ──
  for (const reg of regularisations) {
    const regRef = doc(
      collection(db, `coproprietes/${coproprieteId}/verifications/${verificationId}/regularisations`)
    );
    batchEcriture.set(regRef, {
      exerciceRegularise: String(reg.exerciceRegularise),
      exerciceReception: String(new Date().getFullYear()),
      montantProvisionne: 0,
      montantReel: Math.abs(reg.montantReel),
      delta: reg.montantReel,
      estTropPercu: reg.estTropPercu,
      statut: 'a_regler',
      documentSourceId: reg.documentId,
      documentNom: reg.documentNom,
      dateCreation: now,
      dateModification: now,
    });
  }

  await batchEcriture.commit();

  // ── Mettre à jour les compteurs sur la vérification ──
  const verifRef = doc(db, `coproprietes/${coproprieteId}/verifications`, verificationId);
  await updateDoc(verifRef, {
    nombreAppelsFonds: appelsFonds.length,
    montantTotalAppele,
    montantTotalRegle: 0,
    aRegularisation: regularisations.length > 0,
    dateModification: now,
  });

  return {
    appelsFondsCreees: appelsFonds.length,
    regularisationsCreees: regularisations.length,
  };
}

// ============================================
// STATISTIQUES
// ============================================

/**
 * Compte les verifications par statut
 */
export async function countVerificationsByStatut(
  coproprieteId: string
): Promise<Record<StatutVerification, number>> {
  const verifications = await getVerificationsByCopropriete(coproprieteId);
  
  const counts: Record<StatutVerification, number> = {
    en_cours: 0,
    termine: 0,
    archive: 0,
  };
  
  for (const v of verifications) {
    counts[v.statut]++;
  }
  
  return counts;
}

/**
 * Recupere la derniere verification d'un exercice
 */
export async function getVerificationByExercice(
  coproprieteId: string,
  exercice: string
): Promise<Verification | null> {
  const q = query(
    collection(db, getVerificationsPath(coproprieteId)),
    where('exercice', '==', exercice),
    orderBy('dateCreation', 'desc')
  );
  
  const snapshot = await getDocs(q);
  if (snapshot.empty) {
    return null;
  }
  
  const docSnap = snapshot.docs[0];
  return {
    id: docSnap.id,
    ...docSnap.data(),
  } as Verification;
}

/**
 * Verifie si une verification existe pour un exercice
 */
export async function verificationExistePourExercice(
  coproprieteId: string,
  exercice: string
): Promise<boolean> {
  const verification = await getVerificationByExercice(coproprieteId, exercice);
  return verification !== null;
}

// ============================================
// EXPORT POUR RAPPORT AG
// ============================================

/**
 * Recupere toutes les donnees d'une verification pour generation rapport
 */
export async function getVerificationComplete(
  coproprieteId: string,
  verificationId: string
): Promise<{ verification: Verification; controles: Controle[] } | null> {
  const verification = await getVerificationById(coproprieteId, verificationId);
  if (!verification) return null;
  
  const controles = await getControlesByVerification(coproprieteId, verificationId);
  
  return { verification, controles };
}

/**
 * Enregistre l'URL du rapport PDF genere
 */
export async function enregistrerRapportPdf(
  coproprieteId: string,
  verificationId: string,
  pdfUrl: string
): Promise<void> {
  const docRef = doc(db, getVerificationsPath(coproprieteId), verificationId);
  await updateDoc(docRef, {
    rapportPdfUrl: pdfUrl,
    rapportGenereAt: Timestamp.now(),
    dateModification: Timestamp.now(),
  });
}


// ============================================================================
// SESSION C — APPELS DE FONDS
// ============================================================================

function getAppelsFondsPath(coproprieteId: string, verificationId: string): string {
  return `coproprietes/${coproprieteId}/verifications/${verificationId}/appelsFonds`;
}

function getRegularisationsPath(coproprieteId: string, verificationId: string): string {
  return `coproprietes/${coproprieteId}/verifications/${verificationId}/regularisations`;
}

export async function getAppelsFonds(
  coproprieteId: string,
  verificationId: string
): Promise<AppelFonds[]> {
  const ref = collection(db, getAppelsFondsPath(coproprieteId, verificationId));
  const snapshot = await getDocs(query(ref, orderBy('dateCreation', 'asc')));
  return snapshot.docs.map((d) => ({ id: d.id, ...d.data() } as AppelFonds));
}

export async function ajouterAppelFonds(
  coproprieteId: string,
  verificationId: string,
  data: CreateAppelFondsData,
  userId: string
): Promise<string> {
  const ref = collection(db, getAppelsFondsPath(coproprieteId, verificationId));
  const docRef = await addDoc(ref, {
    ...data,
    statut: 'a_regler',
    montantRegle: 0,
    createdAt: Timestamp.now(),
    updatedAt: Timestamp.now(),
    creePar: userId,
  });
  await recalculerCompteursCockpit(coproprieteId, verificationId);
  return docRef.id;
}

export async function marquerAppelRegle(
  coproprieteId: string,
  verificationId: string,
  appelId: string,
  montantRegle: number,
  datePaiement?: Date,
  note?: string
): Promise<void> {
  const ref = doc(db, getAppelsFondsPath(coproprieteId, verificationId), appelId);
  const updates: Record<string, any> = {
    montantRegle,
    statut: 'regle',
    updatedAt: Timestamp.now(),
  };
  if (datePaiement) updates.datePaiement = Timestamp.fromDate(datePaiement);
  if (note) updates.note = note;
  await updateDoc(ref, updates);
  await recalculerCompteursCockpit(coproprieteId, verificationId);
}

export async function updateStatutAppel(
  coproprieteId: string,
  verificationId: string,
  appelId: string,
  statut: string,
  note?: string
): Promise<void> {
  const ref = doc(db, getAppelsFondsPath(coproprieteId, verificationId), appelId);
  const updates: Record<string, any> = { statut, updatedAt: Timestamp.now() };
  if (note) updates.note = note;
  await updateDoc(ref, updates);
}

export async function supprimerAppelFonds(
  coproprieteId: string,
  verificationId: string,
  appelId: string
): Promise<void> {
  const ref = doc(db, getAppelsFondsPath(coproprieteId, verificationId), appelId);
  await deleteDoc(ref);
  await recalculerCompteursCockpit(coproprieteId, verificationId);
}

// ============================================================================
// SESSION C — REGULARISATIONS
// ============================================================================

export async function getRegularisations(
  coproprieteId: string,
  verificationId: string
): Promise<Regularisation[]> {
  const ref = collection(db, getRegularisationsPath(coproprieteId, verificationId));
  const snapshot = await getDocs(query(ref, orderBy('dateCreation', 'asc')));
  return snapshot.docs.map((d) => ({ id: d.id, ...d.data() } as Regularisation));
}

export async function ajouterRegularisation(
  coproprieteId: string,
  verificationId: string,
  data: CreateRegularisationData
): Promise<string> {
  const ref = collection(db, getRegularisationsPath(coproprieteId, verificationId));
  const docRef = await addDoc(ref, {
    ...data,
    statut: data.estTropPercu ? 'en_attente' : 'a_regler',
    createdAt: Timestamp.now(),
    updatedAt: Timestamp.now(),
  });
  // Marquer la vérification comme ayant une régularisation
  const verifRef = doc(db, getVerificationsPath(coproprieteId), verificationId);
  await updateDoc(verifRef, { aRegularisation: true, dateModification: Timestamp.now() });
  return docRef.id;
}

export async function marquerRegularisationReglee(
  coproprieteId: string,
  verificationId: string,
  regularisationId: string,
  datePaiement?: Date,
  note?: string
): Promise<void> {
  const ref = doc(db, getRegularisationsPath(coproprieteId, verificationId), regularisationId);
  const updates: Record<string, any> = { statut: 'regle', updatedAt: Timestamp.now() };
  if (datePaiement) updates.datePaiement = Timestamp.fromDate(datePaiement);
  if (note) updates.note = note;
  await updateDoc(ref, updates);
}

// ============================================================================
// SESSION C — SYNTHESE COMPTABLE (lecture seule)
// ============================================================================

export async function getSyntheseComptable(
  coproprieteId: string,
  verificationId: string
): Promise<SyntheseComptable> {
  const [appels, reguls] = await Promise.all([
    getAppelsFonds(coproprieteId, verificationId),
    getRegularisations(coproprieteId, verificationId),
  ]);

  const totalAppele = appels.reduce((sum, a) => sum + (a.montantAppele || 0), 0);
  const totalRegle = appels.reduce((sum, a) => sum + (a.montantRegle || 0), 0);
  const totalEnAttente = totalAppele - totalRegle;

  // Grouper par trimestre
  const appelsByTrimestre: Record<string, { appele: number; regle: number }> = {};
  for (const appel of appels) {
    const t = appel.trimestre || 'inconnu';
    if (!appelsByTrimestre[t]) appelsByTrimestre[t] = { appele: 0, regle: 0 };
    appelsByTrimestre[t].appele += appel.montantAppele || 0;
    appelsByTrimestre[t].regle += appel.montantRegle || 0;
  }

  // Régularisation en cours
  const regularisationEnCours = reguls.length > 0
    ? { montant: reguls.reduce((sum, r) => sum + Math.abs(r.delta || 0), 0), estTropPercu: reguls.some((r) => r.estTropPercu) }
    : undefined;

  // Solde final = en attente + régularisations à payer - trop-perçus non encore crédités
  const soldeRegul = reguls
    .filter((r) => !r.estTropPercu && r.statut !== 'regle')
    .reduce((sum, r) => sum + (r.delta || 0), 0);
  const tropPercuNonCredite = reguls
    .filter((r) => r.estTropPercu && r.statut !== 'regle')
    .reduce((sum, r) => sum + Math.abs(r.delta || 0), 0);
  const soldeFinal = totalEnAttente + soldeRegul - tropPercuNonCredite;

  // Score conformité (lecture depuis vérification)
  const verifDoc = await getDoc(doc(db, getVerificationsPath(coproprieteId), verificationId));
  const scoreConformite = verifDoc.exists() ? (verifDoc.data()?.scoreConformite ?? 100) : 100;

  // Synthèse fiscale (agréger ventilationFiscale des appels)
  const appsAvecFiscal = appels.filter((a) => a.ventilationFiscale);
  let syntheseFiscale: SyntheseComptable['syntheseFiscale'] | undefined;
  if (appsAvecFiscal.length > 0) {
    const totalDeductible = appsAvecFiscal.reduce((s, a) => s + (a.ventilationFiscale?.totalDeductible || 0), 0);
    const totalAmortissable = appsAvecFiscal.reduce((s, a) => s + (a.ventilationFiscale?.totalAmortissable || 0), 0);
    const totalNonDeductible = appsAvecFiscal.reduce((s, a) => s + (a.ventilationFiscale?.totalNonDeductible || 0), 0);
    const avertissements = appsAvecFiscal.flatMap((a) => a.ventilationFiscale?.avertissements || []);
    const statut = appsAvecFiscal[0].ventilationFiscale?.statut || 'LMNP';
    syntheseFiscale = { totalDeductible, totalAmortissable, totalNonDeductible, avertissements, statut };
  }

  return {
    totalAppele,
    totalRegle,
    totalEnAttente,
    appelsByTrimestre,
    regularisationEnCours,
    soldeFinal,
    scoreConformite,
    syntheseFiscale,
  };
}

// ============================================================================
// SESSION C — RECALCUL COMPTEURS COCKPIT (privé)
// ============================================================================

async function recalculerCompteursCockpit(
  coproprieteId: string,
  verificationId: string
): Promise<void> {
  const appels = await getAppelsFonds(coproprieteId, verificationId);
  const montantTotalAppele = appels.reduce((sum, a) => sum + (a.montantAppele || 0), 0);
  const montantTotalRegle = appels.reduce((sum, a) => sum + (a.montantRegle || 0), 0);
  const nombreAppelsFonds = appels.length;
  const verifRef = doc(db, getVerificationsPath(coproprieteId), verificationId);
  await updateDoc(verifRef, {
    montantTotalAppele,
    montantTotalRegle,
    nombreAppelsFonds,
    dateModification: Timestamp.now(),
  });
}


